import {
  DisplayRightsCompensationModel,
  DisplayRightsDeliveryChannel,
  DisplayRightsProposalStatus,
  MentionBoostAuthorizationScope,
  MentionBoostAuthorizationSource,
  MentionBoostAuthorizationStatus,
  MentionBoostDestinationType,
  MentionBoostEligibilityStatus,
  MentionBoostPlacementMode,
  MentionBoostProvider,
  MentionBoostReadinessBlockerCode,
  MentionBoostSetupAudienceMode,
  MentionBoostTargetLocationType,
  type DisplayRightsProposalSummary,
  type MentionBoostEligibilityResponse,
  type MentionBoostReadinessResponse,
  type MentionBoostSetupResponse,
} from '@fluenceur/types';

/**
 * Local smoke-test affordance for the whole Partnership Ads boost flow, and the
 * ad-account picker in particular.
 *
 * On a sandbox / Demo Brand the Meta integration lacks the ads permissions
 * (the app is not yet App-Reviewed), so the mention reads "Not eligible" and the
 * boost flow never opens — you cannot reach the launch dialog where the picker
 * lives. Append `?boostDevPreview=1` to the URL on a dev build to make both
 * boost queries return launchable fixtures (eligible mention, a prepared setup,
 * and a multi-account readiness), so the panel, the launch dialog, and the
 * picker can be exercised by hand.
 *
 * Guarded by `import.meta.env.DEV`: Vite strips this branch from production
 * builds, so it can never override real eligibility / setup for a real brand.
 * The launch POST itself still hits the backend and will fail, which honestly
 * marks the boundary: this previews the UI, not an end-to-end launch.
 */
type DevPreviewMode = 'ready' | 'pending';

/**
 * The normalized `boostDevPreview` mode, or null when off / outside a dev build.
 * `1` (or `ready`) previews the launchable flow; `pending` previews the
 * awaiting-creator state so its panel can be eyeballed without real pending data.
 * Any other value is ignored, so a stray/typo'd param cannot swap real data for
 * fixtures.
 */
function devPreviewMode(): DevPreviewMode | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return null;
  }

  const raw = new URLSearchParams(window.location.search).get('boostDevPreview');
  if (raw === 'pending') return 'pending';
  if (raw === '1' || raw === 'ready') return 'ready';
  return null;
}

export function isBoostDevPreviewActive(): boolean {
  return devPreviewMode() !== null;
}

const DEV_READINESS: MentionBoostReadinessResponse = {
  provider: MentionBoostProvider.META,
  ready: true,
  blockers: [{ code: MentionBoostReadinessBlockerCode.AD_ACCOUNT_SELECTION_REQUIRED }],
  adAccounts: [
    {
      id: 'act_demo_main',
      name: 'Demo Brand Main',
      currencyCode: 'EUR',
      paymentReady: true,
      disabled: false,
    },
    {
      id: 'act_demo_us',
      name: 'Demo Brand US',
      currencyCode: 'USD',
      paymentReady: true,
      disabled: false,
    },
    {
      id: 'act_demo_archive',
      name: 'Demo Brand Archive',
      currencyCode: 'EUR',
      paymentReady: false,
      disabled: false,
    },
    {
      id: 'act_demo_legacy',
      name: 'Demo Brand Legacy',
      currencyCode: 'USD',
      paymentReady: true,
      disabled: true,
    },
  ],
  selectedAdAccountId: null,
  advantagePlusAvailable: true,
  requiredPermissions: [],
  missingPermissions: [],
  dsaBeneficiary: 'Demo Brand SAS',
  dsaPayor: null,
};

const DAY_MS = 24 * 60 * 60 * 1000;
const daysFromNow = (days: number) => new Date(Date.now() + days * DAY_MS).toISOString();

/**
 * Display-rights fixtures for the two crossed states worth eyeballing: terms
 * agreed while the ad is authorized, and terms still waiting while Instagram
 * still is too.
 *
 * Dates are relative to now on purpose: a hard-coded acceptance window would
 * silently drift into "expired" and preview a state nobody asked for.
 */
const DEV_DISPLAY_RIGHTS_ACCEPTED: DisplayRightsProposalSummary = {
  id: 'dev-display-rights',
  status: DisplayRightsProposalStatus.ACCEPTED,
  versionNumber: 2,
  lookupKey: 'dev-lookup-key',
  publicUrl: 'https://portal.example/rights/dev-lookup-key',
  compensationModel: DisplayRightsCompensationModel.MONTHLY,
  amountCents: 7500,
  currencyCode: 'EUR',
  durationMonths: 3,
  deliveryChannel: DisplayRightsDeliveryChannel.CONNECTED_EMAIL,
  sentAt: daysFromNow(-6),
  expiresAt: null,
  acceptedAt: daysFromNow(-4),
  declinedAt: null,
  declineReason: null,
  rightsEndAt: daysFromNow(86),
  activeRightsEndAt: daysFromNow(86),
  expiryNoticeSentAt: null,
  rightsEndedAt: null,
  isLive: true,
  offPlatformAttestedAt: null,
  offPlatformAttestedByName: null,
};

const devDisplayRightsSent = (): DisplayRightsProposalSummary => ({
  ...DEV_DISPLAY_RIGHTS_ACCEPTED,
  status: DisplayRightsProposalStatus.SENT,
  versionNumber: 1,
  sentAt: daysFromNow(-2),
  expiresAt: daysFromNow(5),
  acceptedAt: null,
  rightsEndAt: null,
});

function devBoostEligibility(mentionId: string): MentionBoostEligibilityResponse {
  return {
    mentionId,
    status: MentionBoostEligibilityStatus.READY,
    eligible: true,
    canRequestAuthorization: false,
    cta: 'BOOST_THIS_MEDIA',
    blockers: [],
    authorization: {
      defaultScope: MentionBoostAuthorizationScope.ACCOUNT_LEVEL,
      contentOnlyOptionAvailable: false,
      current: {
        id: 'dev-authorization',
        scope: MentionBoostAuthorizationScope.ACCOUNT_LEVEL,
        status: MentionBoostAuthorizationStatus.AUTHORIZED,
        source: MentionBoostAuthorizationSource.INTERNAL_PREFLIGHT,
        mentionId,
        requestedAt: '2026-01-01T00:00:00.000Z',
        respondedAt: '2026-01-01T00:00:00.000Z',
        revokedAt: null,
        expiresAt: null,
        stale: false,
      },
      // Terms agreed AND Instagram authorized: the happy crossed state.
      displayRights: DEV_DISPLAY_RIGHTS_ACCEPTED,
    },
    meta: {
      hasInstagramIntegration: true,
    hasAdsIntegration: true,
      requiredPermissions: [],
      missingPermissions: [],
      canManageBoost: true,
    },
  };
}

function devBoostSetup(mentionId: string): MentionBoostSetupResponse {
  return {
    setup: {
      id: 'dev-setup',
      mentionId,
      authorizationId: 'dev-authorization',
      destinationType: MentionBoostDestinationType.WEBSITE,
      product: null,
      targetUrl: 'https://demo-brand.example/landing',
      budgetAmountInCents: 5000,
      budgetCurrencyCode: 'EUR',
      durationDays: 7,
      audienceMode: MentionBoostSetupAudienceMode.ADVANTAGE_PLUS,
      targetLocations: [
        {
          key: 'FR',
          name: 'France',
          type: MentionBoostTargetLocationType.COUNTRY,
          countryCode: 'FR',
        },
      ],
      targetAgeRange: { min: 18, max: 65 },
      utmParameters: null,
      placementMode: MentionBoostPlacementMode.NEW_CAMPAIGN,
      placementCampaignId: null,
      placementCampaignName: null,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    eligibility: devBoostEligibility(mentionId),
    launchedBoost: null,
    readiness: DEV_READINESS,
  };
}

function devBoostEligibilityPending(mentionId: string): MentionBoostEligibilityResponse {
  return {
    mentionId,
    status: MentionBoostEligibilityStatus.PENDING,
    eligible: false,
    canRequestAuthorization: false,
    cta: null,
    blockers: [],
    authorization: {
      defaultScope: MentionBoostAuthorizationScope.ACCOUNT_LEVEL,
      contentOnlyOptionAvailable: false,
      current: {
        id: 'dev-authorization-pending',
        scope: MentionBoostAuthorizationScope.ACCOUNT_LEVEL,
        status: MentionBoostAuthorizationStatus.PENDING,
        source: MentionBoostAuthorizationSource.INTERNAL_PREFLIGHT,
        mentionId,
        requestedAt: '2026-01-01T00:00:00.000Z',
        respondedAt: null,
        revokedAt: null,
        expiresAt: null,
        stale: false,
      },
      // Both sides still waiting: the state the badges must read plainly.
      displayRights: devDisplayRightsSent(),
    },
    meta: {
      hasInstagramIntegration: true,
    hasAdsIntegration: true,
      requiredPermissions: [],
      missingPermissions: [],
      canManageBoost: true,
    },
  };
}

/**
 * Launchable eligibility fixture for the dev preview, or `null` when the flag is
 * off (the caller then falls back to the real query). `?boostDevPreview=pending`
 * yields the awaiting-creator state instead of the launchable one.
 */
export function devBoostEligibilityOrNull(
  mentionId: string
): MentionBoostEligibilityResponse | null {
  if (devPreviewMode() === 'pending') {
    return devBoostEligibilityPending(mentionId);
  }
  return isBoostDevPreviewActive() ? devBoostEligibility(mentionId) : null;
}

/**
 * Launchable setup-response fixture for the dev preview, or `null` when the flag
 * is off (the caller then falls back to the real query).
 */
export function devBoostSetupOrNull(mentionId: string): MentionBoostSetupResponse | null {
  return isBoostDevPreviewActive() ? devBoostSetup(mentionId) : null;
}
